import type {PhotoRequirement} from './types';
import {effectivePositionModeIn, sentinelsById} from './position-mode';

/** Shape returned by LivePreview.getPageGroups(): one entry per *flow* page. */
export interface PageGroupInfo {
  itemIds: string[];
  pageBreakId?: string;
}

export interface InsertionAnchor {
  /** Id of the requirement the new elements are positioned relative to. */
  anchorId: string | undefined;
  /** Insert before or after the anchor in flow order. */
  placement: 'before' | 'after';
  /** How the anchor was resolved — for tracing/telemetry. */
  reason: 'page-end' | 'page-start' | 'page-break' | 'materialize' | 'walk-back' | 'none';
}

/**
 * Resolve where page-targeted flow elements should be inserted so they RENDER on
 * `activePageIndex`.
 *
 * Anchor strategy — append to the bottom whenever the page has slack, only
 * top-insert when the page is genuinely full:
 *  - LAST flow page → append AFTER its last item (nothing overflowed past it, so
 *    it has slack). The common single-page case.
 *  - A page ended by a HARD PAGE BREAK (the next page starts with a break) → also
 *    append AFTER its last item: the break gives the page slack, so the new element
 *    lands at the bottom and stays on the page. This is the natural "add to the end
 *    of this page" behaviour.
 *  - A FULL overflow page (the next page is an overflow continuation, no break) →
 *    insert BEFORE its first item. Such a page is at capacity, so appending to its
 *    END would push the new element onto the next page; making it the first item
 *    keeps it on the selected page and overflows the page's old tail instead.
 *    **Not a guarantee on its own** — the position is right and the PAGE it lands
 *    on is not, because the flow does not restart at a page edge: the new element
 *    sits immediately after the PREVIOUS page's last item, so pagination puts it
 *    there if that page has room for it. All the overflow tells us is that the
 *    previous page's slack was too small for the item that overflowed, which
 *    leaves it plenty big for something shorter. Cut an element from page 2 and
 *    the slack is bigger still, which is how "paste on page 3" reliably landed on
 *    page 2. `insertFlowElementsOnPage` closes it with a hard break — see
 *    `reason: 'page-start'` there.
 *  - An empty page delimited by a page break → right after the break.
 *
 * `pageGroups` enumerates only FLOW pages. The rendered page count can be larger
 * (the header forces a 2nd page; absolute items can sit on later pages), so
 * `activePageIndex` may point past the end of `pageGroups`. For those phantom pages
 * there is no flow position that lands on them, so we walk BACK and append to the
 * last real flow page — never collapsing to append-at-document-end.
 */
export function resolveInsertionAnchor(
  pageGroups: PageGroupInfo[],
  activePageIndex: number,
): InsertionAnchor {
  if (pageGroups.length === 0 || activePageIndex < 0) {
    return {anchorId: undefined, placement: 'after', reason: 'none'};
  }

  const exact = pageGroups[activePageIndex];
  if (exact) {
    if (exact.itemIds.length > 0) {
      const isLastFlowPage = activePageIndex >= pageGroups.length - 1;
      // The page has slack (→ append at bottom) when it is the last page or is
      // ended by a hard page break; it is full (→ insert at top) only when the
      // next page is a plain overflow continuation.
      const endedByHardBreak = !!pageGroups[activePageIndex + 1]?.pageBreakId;
      return isLastFlowPage || endedByHardBreak
        ? {
            anchorId: exact.itemIds[exact.itemIds.length - 1],
            placement: 'after',
            reason: 'page-end',
          }
        : {anchorId: exact.itemIds[0], placement: 'before', reason: 'page-start'};
    }
    // Empty page delimited by a page break = right after the break.
    if (exact.pageBreakId) {
      return {anchorId: exact.pageBreakId, placement: 'after', reason: 'page-break'};
    }
  }

  // Phantom / empty page (e.g. header- or absolute-induced): walk back to the last
  // real flow page and append to its content.
  for (let p = Math.min(activePageIndex, pageGroups.length - 1); p >= 0; p--) {
    const g = pageGroups[p];
    if (!g) continue;
    if (g.itemIds.length > 0)
      return {anchorId: g.itemIds[g.itemIds.length - 1], placement: 'after', reason: 'walk-back'};
    if (g.pageBreakId) return {anchorId: g.pageBreakId, placement: 'after', reason: 'walk-back'};
  }

  return {anchorId: undefined, placement: 'after', reason: 'none'};
}

/**
 * Insert `newEls` into `requirements` so they VISUALLY RENDER on
 * `activePageIndex`, returning a fresh order-renumbered array. Single source of
 * truth shared by add-element and paste.
 *
 * The guarantee comes from *where on the page* we insert, not just which flow
 * slot — because page membership is decided by height pagination after we mutate:
 *  - Earlier (full) page → the new element becomes the FIRST item of that page, so
 *    pagination keeps it there and the page's old tail overflows instead.
 *  - Phantom page (rendered but with no flow region — e.g. the header-forced 2nd
 *    page, or absolute-only pages) → no flow slot can land there, so we MATERIALIZE
 *    the page by appending page break(s) (built via `makePageBreak`) after the last
 *    flow content. The element is then the first thing after the break → guaranteed
 *    on the selected page. Without a factory we fall back to walk-back placement.
 *  - Last page → natural append at the bottom (it has slack, so it stays on-page).
 */
export function insertFlowElementsOnPage(
  requirements: PhotoRequirement[],
  pageGroups: PageGroupInfo[],
  activePageIndex: number,
  newEls: PhotoRequirement[],
  makePageBreak?: () => PhotoRequirement,
): {requirements: PhotoRequirement[]; anchor: InsertionAnchor} {
  const sorted = [...requirements].sort((a, b) => a.order - b.order);
  const lastFlowPage = pageGroups.length - 1;

  // Phantom page: the selected page has no flow region. Force it to exist with
  // page break(s) so the element renders ON it (the only deterministic guarantee).
  if (makePageBreak && pageGroups.length > 0 && activePageIndex > lastFlowPage) {
    const back = resolveInsertionAnchor(pageGroups, activePageIndex); // → last flow item
    if (back.anchorId) {
      const idx = sorted.findIndex((r) => r.id === back.anchorId);
      if (idx !== -1) {
        const breaks = Array.from({length: activePageIndex - lastFlowPage}, () => makePageBreak());
        sorted.splice(idx + 1, 0, ...breaks, ...newEls);
        return {
          requirements: sorted.map((r, i) => ({...r, order: i})),
          anchor: {anchorId: back.anchorId, placement: 'after', reason: 'materialize'},
        };
      }
    }
  }

  const anchor = resolveInsertionAnchor(pageGroups, activePageIndex);
  if (anchor.anchorId) {
    const idx = sorted.findIndex((r) => r.id === anchor.anchorId);
    if (idx !== -1) {
      const at = anchor.placement === 'before' ? idx : idx + 1;
      /**
       * Pin the page edge the new element is supposed to start at.
       *
       * `page-start` puts it at the top of an overflow-continued page, and that
       * alone does not keep it there: the flow does not restart at a page edge,
       * so the element sits right after the PREVIOUS page's last item and
       * pagination leaves it there if that page has room. The overflow only says
       * the previous page's slack was too small for the item that overflowed —
       * which leaves it big enough for anything shorter, and bigger still once
       * something has been cut from that page. That is the whole of the "cut from
       * page 2, paste on page 3, land on page 2" report.
       *
       * A hard break at the boundary makes it deterministic, and it is the same
       * remedy the phantom-page branch above already uses. It costs nothing
       * visually: the boundary was already there as an overflow, so pinning it
       * moves nothing — it only stops being elastic.
       */
      const pinPage =
        anchor.reason === 'page-start' &&
        !!makePageBreak &&
        // Not if the edge is already pinned. A `page-start` page can already
        // begin with a hard break — its NEXT page being an overflow is what
        // decided the anchor, not its own start — and inserting a second break
        // there would put an empty page between the two. Which is what a second
        // paste onto the same page would have done, every time.
        !pageGroups[activePageIndex]?.pageBreakId;
      sorted.splice(at, 0, ...(pinPage ? [makePageBreak()] : []), ...newEls);
      return {requirements: sorted.map((r, i) => ({...r, order: i})), anchor};
    }
  }

  return {
    requirements: [...sorted, ...newEls].map((r, i) => ({...r, order: i})),
    anchor: {anchorId: undefined, placement: 'after', reason: 'none'},
  };
}

/**
 * Place a mixed set of elements on `activePageIndex` — the paste's whole
 * decision, and pure so it can be reasoned about without a keyboard.
 *
 * **Two kinds of "on this page", answered by different fields.** A flow element
 * is placed by where it sits in the sequence (`insertFlowElementsOnPage`); an
 * absolute one by its `y`, because that is what pagination reads for it. Cut an
 * absolute element from page 2, point at page 3, paste, and it came back on page
 * 2 — nothing had touched the only field that answers for it.
 *
 * A group's CHILDREN follow their sentinel. A child of an absolute group renders
 * absolute whatever it stores (the same rule `usePreviewLayout` applies), so
 * routing it through the flow inserter would leave it at its old `y` — the
 * sentinel would move to the pointed page and its contents would stay behind.
 *
 * Absolute elements are appended rather than positioned: their array order is
 * their stacking order, not their page.
 */
export function placeElementsOnPage(
  requirements: PhotoRequirement[],
  pageGroups: PageGroupInfo[],
  activePageIndex: number,
  newEls: PhotoRequirement[],
  pageHeight: number,
  makePageBreak?: () => PhotoRequirement,
): PhotoRequirement[] {
  // Sentinels from the pasted set, not the document: a clipboard is what is
  // being placed, and a copy whose group was not copied with it answers for
  // itself — see `effectivePositionModeIn`.
  const sentinels = sentinelsById(newEls);
  const isAbsolute = (el: PhotoRequirement) =>
    effectivePositionModeIn(el, sentinels) === 'absolute';

  const targeted = activePageIndex >= 0;
  const absolute = newEls
    .filter(isAbsolute)
    .map((el) => (targeted ? rebaseAbsoluteOnPage(el, activePageIndex, pageHeight) : el));
  const flow = newEls.filter((el) => !isAbsolute(el));

  // Only when there is flow content to place. Calling the inserter with an empty
  // list would still pin a page edge with a hard break — a page break invented
  // by a paste that put nothing in the flow.
  const withFlow =
    targeted && flow.length > 0
      ? insertFlowElementsOnPage(requirements, pageGroups, activePageIndex, flow, makePageBreak)
          .requirements
      : [...requirements, ...flow];

  return [...withFlow, ...absolute].map((r, i) => ({...r, order: i}));
}

/**
 * The same element, moved onto `pageIdx` — for an ABSOLUTE one, whose page is
 * its `y` and not its place in the flow.
 *
 * `insertFlowElementsOnPage` cannot put one of these anywhere: pagination reads
 * `Math.floor(y / pageHeight)` for absolute items (see `absoluteByPage`), so a
 * flow slot decides nothing about which page they render on. Cut an absolute
 * element from page 2, point at page 3, paste — and it came back on page 2,
 * because nothing had touched the only field that answers.
 *
 * The offset within the page is kept and the page index replaced, so a pasted
 * copy lands where the original sat on the page the author pointed at rather
 * than at its top corner. The modulo is floored for a negative `y` — an element
 * dragged above the first page — and wraps a copy nudged past the bottom edge
 * back onto the same page, which is the page that was asked for.
 *
 * `y` unset means the element has never been positioned; there is nothing to
 * rebase and a 0 invented here would move it to the top of the page.
 */
export function rebaseAbsoluteOnPage<T extends {y?: number}>(
  el: T,
  pageIdx: number,
  pageHeight: number,
): T {
  if (el.y == null) return el;
  const withinPage = ((el.y % pageHeight) + pageHeight) % pageHeight;
  return {...el, y: withinPage + pageIdx * pageHeight};
}

/**
 * Reorder whole pages: move the flow page at `fromIdx` to `toIdx`, returning a
 * fresh order-renumbered array (or the original unchanged for a no-op / out-of-range
 * move). Page-level analogue of `reorderFlowElements`.
 *
 * Pages are not first-class — they are runs of flow items delimited by
 * `page_break`s and height overflow (the split is given by `pageGroups`, computed
 * by LivePreview with measured heights). Moving a page therefore means:
 *  - swapping the page's *content* (its flow items) into the new slot, and
 *  - re-normalizing the leading page breaks so page 0 has none and every later
 *    page starts with exactly one. Existing break elements are reused (their ids
 *    are fungible), so no churn beyond the reorder itself.
 *
 * Non-flow elements (absolute items, header/group sentinels) keep their relative
 * array order — preserving z-order — except that absolute items paginate by their
 * `y` coordinate, so items on the affected pages are shifted by ±`pageHeight` to
 * follow the move.
 */
export function moveFlowPage(
  requirements: PhotoRequirement[],
  pageGroups: PageGroupInfo[],
  fromIdx: number,
  toIdx: number,
  pageHeight: number,
): PhotoRequirement[] {
  if (
    fromIdx === toIdx ||
    fromIdx < 0 ||
    toIdx < 0 ||
    fromIdx >= pageGroups.length ||
    toIdx >= pageGroups.length
  ) {
    return requirements;
  }

  const byId = new Map(requirements.map((r) => [r.id, r] as const));

  // Pool of existing leading-break elements in original page order (one per page > 0).
  const breakPool = pageGroups
    .map((g) => (g.pageBreakId ? byId.get(g.pageBreakId) : undefined))
    .filter((b): b is PhotoRequirement => b != null);

  // Move the content block (item ids only — breaks are reassigned below).
  const blocks = pageGroups.map((g) => g.itemIds);
  const [moved] = blocks.splice(fromIdx, 1);
  if (moved) blocks.splice(toIdx, 0, moved);

  // Rebuild the flow id sequence with normalized leading breaks.
  const flowOrder: PhotoRequirement[] = [];
  blocks.forEach((itemIds, p) => {
    if (p > 0) {
      const b = breakPool.shift();
      if (b) flowOrder.push(b);
    }
    for (const id of itemIds) {
      const el = byId.get(id);
      if (el) flowOrder.push(el);
    }
  });

  // Slot the reordered flow elements back into the positions currently occupied
  // by flow elements, leaving everything else where it is.
  const flowIds = new Set(flowOrder.map((r) => r.id));
  const sorted = [...requirements].sort((a, b) => a.order - b.order);
  let qi = 0;
  const rebuilt = sorted.map((el) => (flowIds.has(el.id) ? (flowOrder[qi++] ?? el) : el));

  // Absolute elements paginate by y — shift the affected bands so they follow the move.
  const shiftAbs = (el: PhotoRequirement): PhotoRequirement => {
    if (el.position_mode !== 'absolute' || el.y == null) return el;
    const band = Math.max(0, Math.floor(el.y / pageHeight));
    if (band === fromIdx) return {...el, y: el.y + (toIdx - fromIdx) * pageHeight};
    if (fromIdx < toIdx && band > fromIdx && band <= toIdx) return {...el, y: el.y - pageHeight};
    if (toIdx < fromIdx && band >= toIdx && band < fromIdx) return {...el, y: el.y + pageHeight};
    return el;
  };

  return rebuilt.map((r, i) => ({...shiftAbs(r), order: i}));
}

/**
 * Expand a set of seed ids selected for a flow drag so that whole groups move
 * together: if any seed is a group sentinel, include all its children; if any
 * seed is a group child, include the sentinel and all its siblings. Groups are
 * not nested, so a single pass is sufficient.
 */
export function expandFlowDragSelection(
  requirements: PhotoRequirement[],
  seedIds: string[],
): string[] {
  const ids = new Set(seedIds);
  const childrenOf = (sentinelId: string) => {
    for (const r of requirements) if (r.group_id === sentinelId) ids.add(r.id);
  };
  for (const seed of seedIds) {
    const r = requirements.find((x) => x.id === seed);
    if (!r) continue;
    if (r.type === 'group') {
      ids.add(r.id);
      childrenOf(r.id);
    } else if (r.group_id) {
      const sentinel = requirements.find((s) => s.id === r.group_id && s.type === 'group');
      if (sentinel) {
        ids.add(sentinel.id);
        childrenOf(sentinel.id);
      }
    }
  }
  return [...ids];
}

/**
 * Reorder flow elements: move `movingIds` (as one block, preserving their
 * relative order) to sit before/after `refId`, then renumber `order`. Returns the
 * original array unchanged for no-op drops (ref is part of the moving block, ref
 * missing, or nothing to move).
 */
export function reorderFlowElements(
  requirements: PhotoRequirement[],
  movingIds: string[],
  refId: string,
  placement: 'before' | 'after',
): PhotoRequirement[] {
  const movingSet = new Set(movingIds);
  if (movingSet.has(refId)) return requirements;

  const sorted = [...requirements].sort((a, b) => a.order - b.order);
  const moving = sorted.filter((r) => movingSet.has(r.id));
  if (moving.length === 0) return requirements;

  const rest = sorted.filter((r) => !movingSet.has(r.id));
  const refIdx = rest.findIndex((r) => r.id === refId);
  if (refIdx === -1) return requirements;

  const at = placement === 'before' ? refIdx : refIdx + 1;
  rest.splice(at, 0, ...moving);
  return rest.map((r, i) => ({...r, order: i}));
}
